/**
 * CredentialSubHeader — sub-header for an `ahoy_service_account` slideout.
 *
 * One meta row under the title answering the three questions an admin opens
 * a credential to ask: what kind is it, how far does it reach, and what can it
 * touch — `Kind badge · Reach badge | Reads N types · writes M`. Reach is stated
 * here (a warning-coloured badge when the key reads every user's private
 * records) rather than as a generic property row, so the widest-reaching keys
 * are recognisable at a glance and the row never competes with the overview.
 *
 * Registered on the service-account entry in `ENTITY_TYPE_REGISTRY`;
 * `ahoy_reach` is hidden from the overview so it is stated exactly once.
 */

import {
  credentialReachOf,
  describeScope,
} from '@/lib/api-access/api-access-service';
import { Badge } from '@/lib/base/badges/badges';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { SubHeaderLayout } from './sub-header-layout';

export function CredentialSubHeader({ object }: { object: CrmObject }) {
  const reach = credentialReachOf(object);
  const scope = describeScope(
    getPropertyValue<unknown>(object.properties?.ahoy_permissions)
  );

  const primary = (
    <span className='inline-flex items-center gap-1.5'>
      <Badge color='gray' size='sm'>
        Service account
      </Badge>
      {reach === 'org' && (
        <Badge color='warning' size='sm'>
          Whole org
        </Badge>
      )}
    </span>
  );

  return <SubHeaderLayout primary={primary} secondary={scope} />;
}
